import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, Users, Sparkles, Calendar, HandHeart, BookHeart } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1608476268330-4d92ca748034?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const IMG_FEATURE = "https://images.unsplash.com/photo-1593485589800-579b43749b15?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const IMG_COMMUNITY = "https://images.unsplash.com/photo-1581291519163-a10965fbff8f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const features = [
  { icon: BookOpen, title: "Daily Devotionals", desc: "Meet with God each day through Scripture and heartfelt reflection." },
  { icon: Users, title: "A Gathered Fellowship", desc: "A warm collective walking with Christ, together." },
  { icon: HandHeart, title: "Prayer & Praise", desc: "Share requests, pray for one another, and celebrate answered prayer." },
  { icon: BookHeart, title: "Word & Wellness", desc: "Video teachings and gentle encouragement to rest, reflect, and grow in Christ." },
  { icon: Sparkles, title: "Spirit-Led Reflection", desc: "Gentle, KJV-grounded study questions and prayer prompts." },
  { icon: Calendar, title: "Verse of the Day", desc: "A fresh KJV verse to steady your heart each morning." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAF7EE] text-[#2A2621]">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-[#FAF7EE]/85 backdrop-blur-xl border-b border-[#D5CEBF]/60">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-[#C46B4E]" strokeWidth={1.6} />
            <span className="font-heading text-2xl tracking-tight">The Peace Plot</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-login" className="text-sm font-medium text-[#615A50] hover:text-[#3D5844] px-3 py-2">
              Sign in
            </Link>
            <Link to="/register" data-testid="landing-request-access-top" className="text-sm font-medium px-4 py-2 rounded-full bg-[#3D5844] text-white hover:bg-[#2F4535] transition-colors">
              Request Access
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pt-12 md:pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <p className="text-xs tracking-[0.25em] uppercase text-[#8C6A54] font-semibold mb-5">Chapter &amp; Verse · Rooted in Christ</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#2A2621] font-light">
            A quiet place to grow in faith, together.
          </h1>
          <p className="mt-6 text-lg text-[#615A50] leading-relaxed max-w-xl">
            The Peace Plot is a gentle devotional collective for all believers — a place for
            daily devotionals, Word &amp; Wellness, prayer, and peaceful connection in Christ.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/register" data-testid="landing-request-access" className="px-7 py-3.5 rounded-full bg-[#C46B4E] text-white font-medium hover:bg-[#AC5B40] transition-colors">
              Request to Join
            </Link>
            <Link to="/login" data-testid="landing-signin" className="px-7 py-3.5 rounded-full border border-[#D5CEBF] text-[#2A2621] font-medium hover:bg-[#F3EFE4] transition-colors">
              I'm already a member
            </Link>
          </div>
        </div>
        <div className="fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="relative rounded-[2rem] overflow-hidden border border-[#D5CEBF]">
            <img src={HERO} alt="An open Bible beside a candle in warm light" className="w-full h-[460px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#3D5844]/30 to-transparent" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="max-w-2xl">
          <h2 className="font-heading text-4xl sm:text-5xl text-[#2A2621] tracking-tight">A community rooted in the Word of God</h2>
          <p className="mt-4 text-[#615A50] leading-relaxed">Everything you need to study Scripture, pray together, and walk faithfully — in one peaceful place.</p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div key={f.title} className="rounded-2xl border border-[#D5CEBF] bg-[#F3EFE4] p-7 hover:-translate-y-1 transition-transform duration-300 fade-up" style={{ animationDelay: `${i * 0.06}s` }}>
              <div className="w-11 h-11 rounded-full bg-[#3D5844]/10 flex items-center justify-center">
                <f.icon className="w-5 h-5 text-[#3D5844]" strokeWidth={1.7} />
              </div>
              <h3 className="font-heading text-2xl mt-5 text-[#2A2621]">{f.title}</h3>
              <p className="mt-2 text-[15px] text-[#615A50] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Split image band */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-20 grid md:grid-cols-2 gap-6">
        <div className="rounded-[2rem] overflow-hidden border border-[#D5CEBF] relative h-72">
          <img src={IMG_FEATURE} alt="An open Bible on a sunlit path" className="w-full h-full object-cover" />
        </div>
        <div className="rounded-[2rem] border border-[#D5CEBF] bg-[#E7DFCE] p-10 flex flex-col justify-center">
          <p className="font-heading text-3xl text-[#6B4A38] leading-snug">
            "Thy word is a lamp unto my feet, and a light unto my path."
          </p>
          <p className="mt-4 text-sm text-[#615A50]">— Psalm 119:105 (KJV)</p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-24">
        <div className="rounded-[2rem] overflow-hidden relative">
          <img src={IMG_COMMUNITY} alt="Hands resting on an open Bible in prayer" className="w-full h-80 object-cover" />
          <div className="absolute inset-0 bg-[#2A2621]/55 flex flex-col items-center justify-center text-center px-6">
            <h2 className="font-heading text-4xl sm:text-5xl text-[#FAF7EE]">Come and be welcomed.</h2>
            <p className="mt-3 text-[#FAF7EE]/90 max-w-lg">Membership is by request and welcomed warmly by our community leaders.</p>
            <Link to="/register" data-testid="landing-cta-join" className="mt-7 px-8 py-3.5 rounded-full bg-[#C46B4E] text-white font-medium hover:bg-[#AC5B40] transition-colors">
              Request Access
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#D5CEBF]/60">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 text-center">
          <p className="font-heading text-2xl text-[#6B4A38]">The Peace Plot</p>
          <p className="text-sm text-[#615A50] mt-1">A Christian devotional collective — walking in faith, together.</p>
        </div>
      </footer>
    </div>
  );
}
